// Localized overlay for the Exam Walkthrough page.
//
// English (`data/examWalkthrough.js`) is the structural source of truth: it
// carries the exam→skill→task order, screen-mockup `tt` ids, and the fields
// that must stay English (task names in PTE, Part/Task range labels, digits).
// The five localized JSONs supply translated coaching prose (`title`/`cefr`/
// `lede`/`watch`/`arrow`/`facts` labels) keyed by `tt`, plus a `ui` block of
// static UI strings (skill tabs, buttons, mobile fallback, "What to watch for"
// heading).
//
// `getExamWalkthrough(lang)` returns a shape identical to the English data —
// same object keys, same array order, same fields — so the renderer stays
// oblivious to language. Missing translations fall through to English rather
// than blanking a card, which is the same fallback contract the lesson layer
// already uses.

import zh from '@/data/examWalkthrough_i18n/zh.json';
import ko from '@/data/examWalkthrough_i18n/ko.json';
import es from '@/data/examWalkthrough_i18n/es.json';
import ar from '@/data/examWalkthrough_i18n/ar.json';
import fa from '@/data/examWalkthrough_i18n/fa.json';

import { EXAM_WALKTHROUGH, SKILL_ORDER, SKILL_LABEL } from '@/data/examWalkthrough';

const OVERLAYS = { zh, ko, es, ar, fa };

// The English default `ui` mirrors the strings currently hardcoded in the
// component so a missing key can never blank a label.
const EN_UI = {
  skill_listening: SKILL_LABEL.listening,
  skill_reading: SKILL_LABEL.reading,
  skill_writing: SKILL_LABEL.writing,
  skill_speaking: SKILL_LABEL.speaking,
  header_exam_walked_through: 'The exam, walked through',
  label_what_to_watch_for: 'What to watch for',
  btn_close: 'Close ✕',
  btn_back: '‹ Back',
  btn_next: 'Next ›',
  mobile_title: 'Open this on a laptop.',
  mobile_body: 'The exam walkthrough shows the exam screen and the notes side by side. It needs a wider screen.',
};

// Overlay a single translated task onto its English row, matching by `tt`.
// Only the tutor-facing fields are pulled from the translation so structural
// keys (tt, range, qn, timer, prog, skillTag) are guaranteed to survive.
const TRANSLATABLE_FIELDS = ['title', 'cefr', 'lede', 'watch', 'arrow'];

function mergeTask(enTask, trTask) {
  if (!trTask) return enTask;
  const out = { ...enTask };
  for (const f of TRANSLATABLE_FIELDS) {
    if (trTask[f]) out[f] = trTask[f];
  }
  // `facts` is an array of [n, t] tuples. We keep `n` (numbers / short English
  // labels like "10-minute part") from English by default and only translate
  // the `t` half when the translator supplied a matching tuple at the same
  // index. Guarantees numbers stay numbers.
  if (Array.isArray(trTask.facts) && Array.isArray(enTask.facts)) {
    out.facts = enTask.facts.map((pair, i) => {
      const tr = trTask.facts[i];
      if (Array.isArray(tr) && tr.length === 2) return [tr[0] || pair[0], tr[1] || pair[1]];
      return pair;
    });
  }
  return out;
}

function mergeExam(enExam, trExam) {
  if (!trExam) return enExam;
  const out = { ...enExam };
  if (trExam.label) out.label = trExam.label;
  if (trExam.spineNote) out.spineNote = trExam.spineNote;
  const trSkills = trExam.skills || {};
  for (const skill of SKILL_ORDER) {
    const enList = enExam[skill] || [];
    const trList = trSkills[skill] || [];
    const byTt = Object.fromEntries(trList.map((x) => [x.tt, x]));
    out[skill] = enList.map((t) => mergeTask(t, byTt[t.tt]));
  }
  return out;
}

export function getExamWalkthrough(lang) {
  const overlay = OVERLAYS[lang];
  if (!overlay) {
    return {
      celpip: EXAM_WALKTHROUGH.celpip,
      pte: EXAM_WALKTHROUGH.pte,
      ui: EN_UI,
    };
  }
  return {
    celpip: mergeExam(EXAM_WALKTHROUGH.celpip, overlay.celpip),
    pte: mergeExam(EXAM_WALKTHROUGH.pte, overlay.pte),
    ui: { ...EN_UI, ...(overlay.ui || {}) },
  };
}

// Convenience for consumers that just need the localized skill-tab labels.
export function getSkillLabels(lang) {
  const ui = (OVERLAYS[lang] && OVERLAYS[lang].ui) || {};
  return {
    listening: ui.skill_listening || SKILL_LABEL.listening,
    reading: ui.skill_reading || SKILL_LABEL.reading,
    writing: ui.skill_writing || SKILL_LABEL.writing,
    speaking: ui.skill_speaking || SKILL_LABEL.speaking,
  };
}

export { SKILL_ORDER };
